import React, { useCallback, useState, useMemo, useEffect } from 'react';
import CenteredModal from '../ui/CenteredModal';
import AssignmentDetails from '../assignments/AssignmentDetails';
import { normEndeavorId, normAssignmentId } from '../../contexts/dataUtils';
import { useData } from '../../contexts/DataContext';
import './Next7DaysModal.css';

/**
 * Next7DaysModal — two-column split modal for the "Next 7 Days" progress tile.
 *
 * Left column:  Incomplete assignments (sorted by earliest due date)
 * Right column: Completed assignments (sorted by completion date desc)
 * Bottom:       Course Breakdown with mini progress bars
 *
 * Props:
 *   isOpen:          boolean
 *   onClose:         () => void
 *   incompleteItems: array of { assignment, dueDate } from useAcademicHealth (dueNext7dItems)
 *   completedItems:  array of { assignment, dueDate, completedDate } (next7dCompletedItems)
 */
export default function Next7DaysModal({ isOpen, onClose, incompleteItems, completedItems }) {
  const dataContext = useSafeData();
  const coursesById = useMemo(() => dataContext?.state?.coursesById || {}, [dataContext?.state?.coursesById]);
  const toggleAssignmentCompleted = dataContext?.toggleAssignmentCompleted;
  const markAssignmentCompleted = dataContext?.markAssignmentCompleted;
  const updateAssignment = dataContext?.updateAssignment;
  const deleteAssignment = dataContext?.deleteAssignment;
  const clearCache = dataContext?.clearCache;
  const updateCachedAssignment = dataContext?.updateCachedAssignment;

  // Detail modal state
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  // Optimistic local completion state
  const [localCompleted, setLocalCompleted] = useState({});
  const [togglingIds, setTogglingIds] = useState(new Set());

  // Reset optimistic state when upstream data changes (sync from external sources)
  useEffect(() => {
    setLocalCompleted({});
  }, [incompleteItems, completedItems]);

  const total = incompleteItems.length + completedItems.length;
  const completed = completedItems.length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  let barColor;
  if (pct >= 80) barColor = 'green';
  else if (pct >= 40) barColor = 'yellow';
  else barColor = 'red';

  // ── Course name helper ──
  const getCourseName = useCallback((a) => {
    const cid = normEndeavorId(a);
    if (!cid) return null;
    const course = coursesById[cid] || coursesById[String(cid)];
    return course?.name || course?.title || null;
  }, [coursesById]);

  // ── Course Breakdown ──
  const courseBreakdown = useMemo(() => {
    const map = new Map();
    const process = (items, isDone) => {
      items.forEach(item => {
        const a = item.assignment;
        const cid = normEndeavorId(a) || 'unknown';
        const cn = (() => {
          const c = normEndeavorId(a);
          if (!c) return 'Unknown Course';
          const course = coursesById[c] || coursesById[String(c)];
          return course?.name || course?.title || 'Unknown Course';
        })();
        if (!map.has(cid)) map.set(cid, { name: cn, total: 0, completed: 0, remaining: 0 });
        const e = map.get(cid);
        e.total++;
        if (isDone) e.completed++; else e.remaining++;
      });
    };
    process(incompleteItems, false);
    process(completedItems, true);
    return Array.from(map.values()).sort((a, b) => b.remaining - a.remaining);
  }, [incompleteItems, completedItems, coursesById]);

  // ── Toggle completion optimistically ──
  const handleToggle = useCallback(async (a, e) => {
    e.stopPropagation();
    if (!toggleAssignmentCompleted) return;
    const id = normAssignmentId(a);
    if (!id || togglingIds.has(id)) return;

    const currentlyDone = localCompleted[id] ?? isCompleted(a);
    const willBeDone = !currentlyDone;

    setLocalCompleted(prev => ({ ...prev, [id]: willBeDone }));
    setTogglingIds(prev => new Set(prev).add(id));

    try {
      await toggleAssignmentCompleted(a, { source: 'next7d-modal' });
      window.dispatchEvent(new Event('assignments:updated'));
    } catch (err) {
      setLocalCompleted(prev => ({ ...prev, [id]: currentlyDone }));
      console.error('Toggle failed:', err);
    } finally {
      setTogglingIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    }
  }, [toggleAssignmentCompleted, localCompleted, togglingIds]);

  // ── Row click → open detail ──
  const handleRowClick = useCallback((a) => {
    setSelectedAssignment(a);
  }, []);

  // ── Assignment detail callbacks ──
  const handleAssignmentMarkComplete = useCallback(async (assignmentData) => {
    const id = assignmentData.assignmentId || assignmentData.id;
    const willBeCompleted = assignmentData.completed;
    if (willBeCompleted && markAssignmentCompleted) {
      await markAssignmentCompleted(id);
    } else if (updateAssignment) {
      await updateAssignment(id, assignmentData);
    }
    if (clearCache) { clearCache('assignments'); clearCache(`assignment_${id}`); }
    setSelectedAssignment(null);
    window.dispatchEvent(new Event('assignments:updated'));
  }, [markAssignmentCompleted, updateAssignment, clearCache]);

  const handleAssignmentUpdate = useCallback((updatedData) => {
    setSelectedAssignment(updatedData);
  }, []);

  const handleAssignmentSaved = useCallback(async (serverData, id) => {
    setSelectedAssignment(null);
    if (updateCachedAssignment) { updateCachedAssignment(id, serverData); }
    window.dispatchEvent(new Event('assignments:updated'));
  }, [updateCachedAssignment]);

  return (
    <>
      <CenteredModal isOpen={isOpen} onClose={onClose} title="Next 7 Days — Assignment Progress">
        <div className="n7m">
          {/* ── Progress summary ── */}
          <div className="n7m-summary">
            <div className="n7m-summaryText">
              <span className="n7m-summaryCount">{completed} / {total}</span>{' '}completed
            </div>
            <div className="n7m-progressBar">
              <div
                className={`n7m-progressFill n7m-progressFill--${barColor}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="n7m-summaryRemaining">
              {total - completed} remaining
            </div>
          </div>

          {total === 0 ? (
            <div className="n7m-empty">No assignments due in the next 7 days.</div>
          ) : (
            <div className="n7m-columns">
              {/* ── Left: Incomplete ── */}
              <div className="n7m-col">
                <div className="n7m-colHeader">
                  <span className="n7m-colTitle">Incomplete</span>
                  <span className="n7m-colCount">({incompleteItems.length})</span>
                </div>
                {incompleteItems.length === 0 ? (
                  <div className="n7m-colEmpty">All done — nice work!</div>
                ) : (
                  <div className="n7m-colList">
                    {incompleteItems.map((item, i) => {
                      const a = item.assignment;
                      const id = normAssignmentId(a);
                      const done = localCompleted[id] ?? false;
                      const toggling = togglingIds.has(id);
                      const title = a.title || a.name || 'Assignment';
                      const courseName = getCourseName(a);

                      return (
                        <div
                          key={id || i}
                          className={`n7m-row ${done ? 'n7m-row--completed' : ''}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleRowClick(a)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleRowClick(a); }}
                        >
                          <span
                            className={`n7m-check ${done ? 'n7m-check--done' : ''} ${toggling ? 'n7m-check--busy' : ''}`}
                            role="checkbox"
                            aria-checked={done}
                            aria-label={done ? 'Mark incomplete' : 'Mark complete'}
                            tabIndex={0}
                            onClick={(e) => handleToggle(a, e)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle(a, e); } }}
                          >
                            {done && (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </span>
                          <div className="n7m-body">
                            <span className={`n7m-title ${done ? 'n7m-title--done' : ''}`}>{title}</span>
                            {courseName && <span className="n7m-subtitle">{courseName}</span>}
                          </div>
                          <span className="n7m-date">{fmtDate(item.dueDate)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ── Right: Completed ── */}
              <div className="n7m-col">
                <div className="n7m-colHeader">
                  <span className="n7m-colTitle">Completed</span>
                  <span className="n7m-colCount">({completedItems.length})</span>
                </div>
                {completedItems.length === 0 ? (
                  <div className="n7m-colEmpty">None completed yet.</div>
                ) : (
                  <div className="n7m-colList">
                    {completedItems.map((item, i) => {
                      const a = item.assignment;
                      const id = normAssignmentId(a);
                      const title = a.title || a.name || 'Assignment';
                      const courseName = getCourseName(a);

                      return (
                        <div
                          key={id || i}
                          className="n7m-row n7m-row--completed"
                          role="button"
                          tabIndex={0}
                          onClick={() => handleRowClick(a)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleRowClick(a); }}
                        >
                          <span className="n7m-check n7m-check--done">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </span>
                          <div className="n7m-body">
                            <span className="n7m-title n7m-title--done">{title}</span>
                            {courseName && <span className="n7m-subtitle">{courseName}</span>}
                          </div>
                          <span className="n7m-date">
                            {item.completedDate ? `Completed ${fmtDate(item.completedDate)}` : 'Completed'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Course Breakdown ── */}
          {courseBreakdown.length > 0 && (
            <div className="n7m-breakdown">
              <div className="n7m-breakdownTitle">Course Breakdown</div>
              {courseBreakdown.map((c, i) => {
                const coursePct = c.total > 0 ? Math.round((c.completed / c.total) * 100) : 0;
                return (
                  <div key={i} className="n7m-breakdownRow">
                    <div className="n7m-breakdownInfo">
                      <span className="n7m-breakdownName">{c.name}</span>
                      <span className="n7m-breakdownStats">
                        {c.remaining} remaining / {c.total} total
                      </span>
                    </div>
                    <div className="n7m-breakdownBar">
                      <div className="n7m-breakdownFill" style={{ width: `${coursePct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Footer ── */}
          <div className="n7m-footer">
            <span className="n7m-footerCount">
              {total} assignment{total !== 1 ? 's' : ''} this week
            </span>
            <button className="n7m-closeBtn" onClick={onClose}>Close</button>
          </div>
        </div>
      </CenteredModal>

      {/* Assignment detail modal (stacks on top) */}
      <CenteredModal
        isOpen={!!selectedAssignment}
        onClose={() => setSelectedAssignment(null)}
        title={selectedAssignment?.title || selectedAssignment?.name}
        onDelete={selectedAssignment && deleteAssignment ? async () => {
          await deleteAssignment(selectedAssignment.assignmentId || selectedAssignment.id);
          setSelectedAssignment(null);
          window.dispatchEvent(new Event('assignments:updated'));
        } : undefined}
      >
        {selectedAssignment && (
          <AssignmentDetails
            assignment={selectedAssignment}
            onClose={() => setSelectedAssignment(null)}
            onUpdate={handleAssignmentUpdate}
            onSaved={handleAssignmentSaved}
            onMarkComplete={handleAssignmentMarkComplete}
          />
        )}
      </CenteredModal>
    </>
  );
}

/* ── Helpers ── */

function isCompleted(a) {
  if (!a) return false;
  if (a.status === 'completed' || a.status === 'Completed') return true;
  if (a.completedAt != null || a.completed_at != null) return true;
  if (a.completed) return true;
  if (a.submissionStatus === 'Completed') return true;
  if (a.completionStatus === 'Completed') return true;
  return false;
}

function fmtDate(d) {
  try {
    if (!(d instanceof Date)) d = new Date(d);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

function useSafeData() {
  try {
    return useData();
  } catch { return {}; }
}
